import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbghboh653ivfvnqgg";
const MAX_CONTENT = 500;

const types = ["爆料投稿", "内容纠错", "内容合作", "其他"];

export default function SubmissionForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setFormError("");
      setStatus("success");
      return;
    }

    const nickname = String(formData.get("nickname") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const content = String(formData.get("content") ?? "").trim();

    if (!nickname) {
      setStatus("error");
      setFormError("请填写你的称呼，方便我们在核对时联系你。");
      return;
    }
    if (!email) {
      setStatus("error");
      setFormError("请填写邮箱，内容核对结果会通过邮件回复。");
      return;
    }
    if (!content) {
      setStatus("error");
      setFormError("请把事件大致情况写一下，写清时间和作品就够了。");
      return;
    }
    if (content.length > MAX_CONTENT) {
      setStatus("error");
      setFormError(`内容不超过 ${MAX_CONTENT} 字，当前 ${content.length} 字，请精简后再提交。`);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请确认没有填写多余信息后重试。"
            : serverMsg || "提交失败，请稍后重试，或直接发送邮件到 tips@guawang30.com。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常导致提交失败，请检查网络后重试，或直接发送邮件到 tips@guawang30.com。");
    }
  };

  if (status === "success") {
    return (
      <div className="bg-background-100 border border-background-200 rounded-lg p-8 text-center">
        <span className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-800">
          <i className="ri-check-line text-2xl"></i>
        </span>
        <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2">已收到，谢谢</h3>
        <p className="text-sm text-foreground-600 leading-relaxed max-w-md mx-auto">
          我们会在人工核对后决定是否更新条目。如果信息有效，你投稿的线索可能会出现在下一期盘点里。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-800 whitespace-nowrap hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="guawang30-submission-form"
      className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6"
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="nickname" className="block text-xs text-foreground-600 mb-1.5">
            你的称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-xs text-foreground-600 mb-1.5">
            邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="用于接收核对结果"
            className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="type" className="block text-xs text-foreground-600 mb-1.5">
            提交类型
          </label>
          <select
            id="type"
            name="type"
            defaultValue={types[0]}
            className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 cursor-pointer"
          >
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="subject" className="block text-xs text-foreground-600 mb-1.5">
            事件标题或链接
          </label>
          <input
            id="subject"
            name="subject"
            type="text"
            placeholder="例如：某剧换角传闻 / 原帖链接"
            className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="content" className="block text-xs text-foreground-600 mb-1.5">
          具体内容 <span className="text-primary-600">*</span>
        </label>
        <textarea
          id="content"
          name="content"
          rows={5}
          required
          maxLength={MAX_CONTENT}
          placeholder="写清事件大致时间、涉及的作品或节目，以及你想补充或更正的信息即可。"
          className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 leading-relaxed resize-y focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        ></textarea>
        <p className="mt-1.5 text-xs text-foreground-500">最多 {MAX_CONTENT} 字，超出后无法提交。</p>
      </div>

      <div className="form-honeypot-field" aria-hidden="true">
        <label htmlFor="website_alt">个人网站</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" && formError && (
        <p
          role="alert"
          className="mt-4 flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-2.5"
        >
          <span className="w-4 h-4 mt-0.5 flex items-center justify-center shrink-0">
            <i className="ri-error-warning-line"></i>
          </span>
          {formError}
        </p>
      )}

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? "提交中…" : "提交内容"}
        </button>
        <p className="text-xs text-foreground-500">
          提交后会人工核对，不代表一定会收录。涉及隐私信息的内容请勿提交。
        </p>
      </div>
    </form>
  );
}